import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import RestoreRoundedIcon from '@mui/icons-material/RestoreRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import type { UseQueryResult } from '@tanstack/react-query';

import type { NormalizedApiError } from '@/shared/api';
import { CompactTablePagination, EmptyState, ErrorState, LoadingState } from '@/shared/ui';

import { formatDateTime } from '../../lib/format';
import { CATALOG_VERSION_SOURCE_LABELS, catalogVersionSourceColor } from '../../model/options';
import type { CatalogVersionDetail, CatalogVersionSummaryPage } from '../../model/types';

const PAGE_SIZE = 10;

export interface VersionHistoryRestoreConfig {
  /** Whether the current user may restore (`catalog.manage`) — false renders a read-only notice instead. */
  canRestore: boolean;
  isPending: boolean;
  onRestore: (number: number) => void;
}

export interface VersionHistoryDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Human-readable subject shown under the header, e.g. the product/category name. */
  subjectLabel: string;
  entityId: string;
  usePage: (
    entityId: string,
    params: { page: number; size: number },
    options: { enabled: boolean },
  ) => UseQueryResult<CatalogVersionSummaryPage, NormalizedApiError>;
  useDetail: (
    entityId: string,
    number: number | undefined,
    options: { enabled: boolean },
  ) => UseQueryResult<CatalogVersionDetail, NormalizedApiError>;
  /** Omit entirely to hide the Restore action (categories have no restore endpoint). */
  restore?: VersionHistoryRestoreConfig;
}

/** Renders a diff cell value: primitives as-is, objects/arrays as compact JSON, absent as an em dash. */
function prettyPrintDiffValue(value: unknown): string {
  if (value === undefined || value === null) return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/**
 * Version-history drawer, reused for BOTH products and categories (epic-catalog-005,
 * user-story-merchant-portal-032) — the data-fetching hooks are injected as props (`usePage`/
 * `useDetail`) rather than imported directly, so this one component serves both entity kinds
 * without depending on product- or category-specific API modules. `restore` is omitted entirely
 * for categories, which have no restore endpoint (list + detail only).
 *
 * Two internal views: a paged list (click a row for its full diff) and a diff detail (field | old
 * | new, values pretty-printed). The Restore action only appears in the detail view.
 */
export function VersionHistoryDrawer({
  open,
  onClose,
  subjectLabel,
  entityId,
  usePage,
  useDetail,
  restore,
}: VersionHistoryDrawerProps) {
  const { t } = useTranslation('catalog');
  const [page, setPage] = useState(0);
  const [selectedNumber, setSelectedNumber] = useState<number | null>(null);
  const [restoreTarget, setRestoreTarget] = useState<number | null>(null);

  // Reset to the list view whenever the drawer (re)opens — including for a different entity, e.g.
  // clicking the history icon on a different category row while a prior selection is stale.
  useEffect(() => {
    if (open) {
      setSelectedNumber(null);
      setPage(0);
    }
  }, [open, entityId]);

  const pageQuery = usePage(entityId, { page, size: PAGE_SIZE }, { enabled: open });
  const detailQuery = useDetail(entityId, selectedNumber ?? undefined, {
    enabled: open && selectedNumber !== null,
  });

  const versions = pageQuery.data?.content ?? [];
  const total = pageQuery.data?.totalElements ?? 0;
  const detail = detailQuery.data;

  const confirmRestore = () => {
    if (restoreTarget === null || !restore) return;
    restore.onRestore(restoreTarget);
    setRestoreTarget(null);
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        slotProps={{
          paper: { sx: { width: { xs: '100vw', sm: 560 }, display: 'flex', flexDirection: 'column' } },
        }}
      >
        {/* ── Header (sticky) ─────────────────────────────────────────────── */}
        <Box
          sx={{
            px: 2.5,
            py: 1.75,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexShrink: 0,
            bgcolor: 'background.paper',
          }}
        >
          {selectedNumber !== null ? (
            <IconButton
              size="small"
              onClick={() => setSelectedNumber(null)}
              aria-label={t('versionHistoryDrawer.backAria')}
            >
              <ArrowBackRoundedIcon fontSize="small" />
            </IconButton>
          ) : (
            <HistoryRoundedIcon color="action" aria-hidden />
          )}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, lineHeight: 1.35 }} noWrap>
              {selectedNumber !== null
                ? t('versionHistoryDrawer.versionTitle', { number: selectedNumber })
                : t('versionHistoryDrawer.title')}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {subjectLabel}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" aria-label={t('common:action.close')}>
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* ── Body (scrollable) ──────────────────────────────────────────────── */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5 }}>
          {selectedNumber === null ? (
            // ── List view ────────────────────────────────────────────────────
            pageQuery.isLoading ? (
              <LoadingState label={t('versionHistoryDrawer.loading')} />
            ) : pageQuery.isError ? (
              <ErrorState
                title={t('versionHistoryDrawer.loadError.title')}
                message={pageQuery.error?.message ?? t('common:error.unknown')}
                onRetry={() => void pageQuery.refetch()}
              />
            ) : versions.length === 0 ? (
              <EmptyState
                title={t('versionHistoryDrawer.empty.title')}
                description={t('versionHistoryDrawer.empty.description')}
                icon={<HistoryRoundedIcon />}
              />
            ) : (
              <Stack spacing={1.5}>
                <TableContainer>
                  <Table size="small" aria-label={t('versionHistoryDrawer.title')}>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('versionHistoryDrawer.table.version')}</TableCell>
                        <TableCell>{t('versionHistoryDrawer.table.author')}</TableCell>
                        <TableCell>{t('versionHistoryDrawer.table.source')}</TableCell>
                        <TableCell>{t('versionHistoryDrawer.table.createdAt')}</TableCell>
                        <TableCell align="right">{t('versionHistoryDrawer.table.changes')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {versions.map((version) => (
                        <TableRow
                          key={version.number}
                          hover
                          onClick={() => setSelectedNumber(version.number)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              #{version.number}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 140 }}>
                              {version.author}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              variant="outlined"
                              color={catalogVersionSourceColor(version.source)}
                              label={t(CATALOG_VERSION_SOURCE_LABELS[version.source])}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap>
                              {formatDateTime(version.createdAt)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" onClick={(e) => e.stopPropagation()} sx={{ cursor: 'default' }}>
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                              <Chip
                                size="small"
                                label={t('versionHistoryDrawer.changedFieldsCount', {
                                  count: version.changedFields.length,
                                })}
                              />
                              {version.restoredFrom != null ? (
                                <Tooltip
                                  title={t('versionHistoryDrawer.restoredFromTooltip', {
                                    number: version.restoredFrom,
                                  })}
                                >
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    color="info"
                                    label={t('versionHistoryDrawer.restoredFromChip', {
                                      number: version.restoredFrom,
                                    })}
                                  />
                                </Tooltip>
                              ) : null}
                              {version.truncated ? (
                                <Tooltip title={t('versionHistoryDrawer.truncatedTooltip')}>
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    color="warning"
                                    label={t('versionHistoryDrawer.truncatedChip')}
                                  />
                                </Tooltip>
                              ) : null}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <CompactTablePagination
                  count={total}
                  page={page}
                  rowsPerPage={PAGE_SIZE}
                  hideRowsPerPage
                  onPageChange={(_e, next) => setPage(next)}
                  onRowsPerPageChange={() => {}}
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}–${to} ${t('common:pagination.of')} ${count}`
                  }
                />
              </Stack>
            )
          ) : // ── Detail view ─────────────────────────────────────────────────
          detailQuery.isLoading ? (
            <LoadingState label={t('versionHistoryDrawer.loadingDetail')} />
          ) : detailQuery.isError ? (
            <ErrorState
              title={t('versionHistoryDrawer.loadError.title')}
              message={detailQuery.error?.message ?? t('common:error.unknown')}
              onRetry={() => void detailQuery.refetch()}
            />
          ) : detail ? (
            <Stack spacing={2}>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center">
                <Chip
                  size="small"
                  variant="outlined"
                  color={catalogVersionSourceColor(detail.source)}
                  label={t(CATALOG_VERSION_SOURCE_LABELS[detail.source])}
                />
                <Typography variant="body2" color="text.secondary">
                  {detail.author} · {formatDateTime(detail.createdAt)}
                </Typography>
              </Stack>
              {detail.restoredFrom != null ? (
                <Alert severity="info">
                  {t('versionHistoryDrawer.restoredFromNotice', { number: detail.restoredFrom })}
                </Alert>
              ) : null}
              {detail.truncated ? (
                <Alert severity="warning">{t('versionHistoryDrawer.truncatedNotice')}</Alert>
              ) : null}
              {Object.keys(detail.diff).length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t('versionHistoryDrawer.noChanges')}
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small" aria-label={t('versionHistoryDrawer.diffTable.field')}>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('versionHistoryDrawer.diffTable.field')}</TableCell>
                        <TableCell>{t('versionHistoryDrawer.diffTable.oldValue')}</TableCell>
                        <TableCell>{t('versionHistoryDrawer.diffTable.newValue')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(detail.diff).map(([field, entry]) => (
                        <TableRow key={field}>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            <Box component="code" sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}>
                              {field}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top', maxWidth: 180 }}>
                            <Box
                              component="pre"
                              sx={{
                                m: 0,
                                fontFamily: 'JetBrains Mono, monospace',
                                fontSize: 12,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                              }}
                            >
                              {prettyPrintDiffValue(entry?.old)}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top', maxWidth: 180 }}>
                            <Box
                              component="pre"
                              sx={{
                                m: 0,
                                fontFamily: 'JetBrains Mono, monospace',
                                fontSize: 12,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                              }}
                            >
                              {prettyPrintDiffValue(entry?.new)}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Stack>
          ) : null}
        </Box>

        {/* ── Footer — Restore action (detail view only) ───────────────────── */}
        {selectedNumber !== null && restore ? (
          <Box
            sx={{
              px: 2.5,
              py: 1.75,
              borderTop: (theme) => `1px solid ${theme.palette.divider}`,
              flexShrink: 0,
              bgcolor: 'background.paper',
            }}
          >
            {restore.canRestore ? (
              <Button
                fullWidth
                variant="outlined"
                startIcon={restore.isPending ? <CircularProgress size={16} /> : <RestoreRoundedIcon />}
                disabled={restore.isPending || detailQuery.isLoading || !detail}
                onClick={() => setRestoreTarget(selectedNumber)}
              >
                {t('versionHistoryDrawer.restoreButton', { number: selectedNumber })}
              </Button>
            ) : (
              <Alert severity="info" sx={{ '& .MuiAlert-message': { fontSize: '0.8125rem' } }}>
                {t('versionHistoryDrawer.restoreNoPermission')}
              </Alert>
            )}
          </Box>
        ) : null}
      </Drawer>

      {/* ── Restore confirm ───────────────────────────────────────────────── */}
      <Dialog open={restoreTarget !== null} onClose={() => setRestoreTarget(null)}>
        <DialogTitle>{t('versionHistoryDrawer.restoreDialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('versionHistoryDrawer.restoreDialog.body', { number: restoreTarget ?? 0 })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreTarget(null)}>{t('common:action.cancel')}</Button>
          <Button variant="contained" onClick={confirmRestore} disabled={restore?.isPending}>
            {t('versionHistoryDrawer.restoreDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
